import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { APIVersion, UpdateStatus } from "../../types/resources";
import { ConfigurationResponse } from "../../types/rest";
import { applyResources } from "../../utils/rest/apply-resources";

interface ConfirmUpgradeDialogProps extends DialogProps {
  configName: string;
  onClose: () => void;
}

export const ConfirmUpgradeDialog: React.FC<ConfirmUpgradeDialogProps> = ({
  configName,
  ...dialogProps
}) => {
  const { enqueueSnackbar } = useSnackbar();
  async function handleUpgrade() {
    try {
      await upgradeToV2(configName);
      enqueueSnackbar("Configuration upgraded successfully.", {
        variant: "success",
      });
      dialogProps.onClose();
    } catch (e) {
      if (e instanceof Error) {
        enqueueSnackbar("Failed to upgrade configuration: " + e.message, {
          variant: "error",
        });
      } else {
        enqueueSnackbar("Failed to upgrade configuration.", {
          variant: "error",
        });
      }
    }
  }
  return (
    <Dialog {...dialogProps}>
      <DialogTitle>
        Upgrade Configuration <strong>{configName}</strong> to API Version 2?
      </DialogTitle>

      <DialogContent>
        <Typography>
          API Version 2 is still in alpha and may not be fully supported.
          Consider first duplicating this configuration and upgrading the
          duplicate.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={dialogProps.onClose}>Cancel</Button>
        <Button color="primary" variant="contained" onClick={handleUpgrade}>
          Upgrade
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * UpgradeToV2 upgrades the configuration of the given name to API version v2alpha.
 * It first requests the configuration from the API, changes the apiVersion field, and
 * uses /apply to update the configuration.
 */
export async function upgradeToV2(configName: string) {
  const resp = await fetch(`/v1/configurations/${configName}`);
  if (resp.status !== 200) {
    throw new Error("failed to fetch configuration with name " + configName);
  }

  const { configuration } = (await resp.json()) as ConfigurationResponse;
  configuration.apiVersion = APIVersion.V2Alpha;

  const { updates } = await applyResources([configuration]);
  if (updates.length !== 1 || updates[0].status !== UpdateStatus.CONFIGURED) {
    throw new Error(
      "failed to apply new configuration, status " +
        updates[0].status +
        ". " +
        updates[0].reason,
    );
  }
}
